/**
 * Feature Registry - Dynamic Feature Management from Backend Module Definitions
 *
 * This service replaces the hardcoded feature definitions in getFeatures.ts (1650+ lines)
 * by consuming feature metadata from the backend via /api/v1/auth/context.
 *
 * Key Responsibilities:
 * - Load feature definitions from AuthContext
 * - Provide fast feature lookups by key
 * - Resolve icons dynamically based on theme
 * - Map legacy site names to module features
 * - Handle feature hierarchies (parent/child)
 *
 * @see BACKEND_GETFEATURESV3_SPEC.md for backend contract
 */

import type {
  AuthContextResponse,
  ModuleDefinitionDTO,
  ModuleIconSet,
  ModuleFeatureDTO,
} from '@/types/auth_context';
import { apiAzureUrl } from '@/uptk/core/api/contants';
import { getModuleFeatureKeys } from '@/uptk/core/modules/registry';

const isAbsoluteUrl = (value: string) => /^[a-z][a-z\d+.-]*:/i.test(value) || value.startsWith('//');

/**
 * Feature definition consumed by UI components
 * Extends backend ModuleFeatureDTO with computed fields
 *
 * Computed Fields (not in backend response):
 * - moduleCode: Populated from parent ModuleDefinitionDTO.code
 * - svgIcon: Resolved from ModuleIconSet based on theme
 */
export type FeatureDefinition = ModuleFeatureDTO & {
  moduleCode: string;
  svgIcon?: string;
};

/**
 * Legacy site name to module code mapping
 * Used during migration phase to maintain backward compatibility
 */
const SITE_TO_MODULE_MAP: Record<string, string[]> = {
  // Finance mappings
  finance: ['finance-portfolio'],
  'finance2': ['finance-portfolio'],
  'finance-preview': ['finance-portfolio'],

  // Project/PMO mappings
  project: ['pmo'],
  'project2': ['pmo'],
  'project-admin': ['pmo'],
  projectreporting: ['pmo'],
  psf: ['pmo'],
  psfline: ['pmo'],

  // Business/Application/Infrastructure mappings (legacy and current module codes)
  business: ['cbm', 'cbm-business'],
  'business-admin': ['cbm', 'cbm-business'],
  beal: ['cbm', 'cbm-business'],
  bcm: ['cbm', 'cbm-business'],
  application: ['cbm', 'cbm-application'],
  'application-admin': ['cbm', 'cbm-application'],
  seal: ['cbm', 'cbm-application'],
  sla: ['cbm', 'cbm-application'],
  module: ['cbm', 'cbm-application'],
  infrastructure: ['cbm', 'cbm-infrastructure'],
  'verum-admin': ['cbm', 'cbm-infrastructure'],
  verumserver: ['cbm', 'cbm-infrastructure'],
  verumdbi: ['cbm', 'cbm-infrastructure'],
  verummiddleware: ['cbm', 'cbm-infrastructure'],
  verumpkg: ['cbm', 'cbm-infrastructure'],

  // Sourcing mappings
  servicetype: ['sourcing'],
  supplier: ['sourcing'],
  contract: ['sourcing'],
  order: ['sourcing'],

  // Parameter/admin mappings (legacy and current module codes)
  parameter: ['core', 'core-parameters'],
  'parameter2': ['core', 'core-parameters'],
  'menu-parameter': ['core', 'core-parameters'],
  admin: ['core', 'core-admin'],

  // Portal views (show features from multiple modules)
  portal: ['finance-portfolio', 'pmo', 'cbm', 'sourcing', 'compliance', 'core'],
  home: ['finance-portfolio', 'pmo', 'cbm', 'sourcing', 'compliance', 'core'],
  adminportal: ['core'],

  // Compliance mappings
  compliance: ['compliance', 'compliance-core'],
  iso22301: ['compliance-addon-iso22301'],
  iso27001: ['compliance-addon-iso27001'],

};

/**
 * Singleton Feature Registry Service
 */
class FeatureRegistry {
  private features: Map<string, FeatureDefinition> = new Map();
  private moduleFeatures: Map<string, FeatureDefinition[]> = new Map();
  private loaded = false;

  private resolveIso22301FallbackFeature(key?: string) {
    const normalizedKey = (key || '').toLowerCase();

    const fallbackMap: Record<
      string,
      { domain: 'business' | 'application' | 'infrastructure'; entity: string; includeYear: boolean }
    > = {
      beal: { domain: 'business', entity: 'beals', includeYear: false },
      bcm: { domain: 'business', entity: 'bcms', includeYear: false },
      servicetype: { domain: 'business', entity: 'servicetypes', includeYear: false },
      seal: { domain: 'application', entity: 'seals', includeYear: false },
      sealmodule: { domain: 'application', entity: 'sealmodules', includeYear: false },
      sla: { domain: 'application', entity: 'slas', includeYear: false },
      verumserver: { domain: 'infrastructure', entity: 'verumservers', includeYear: false },
      verumdbi: { domain: 'infrastructure', entity: 'verumdbis', includeYear: false },
      verummiddleware: { domain: 'infrastructure', entity: 'verummiddlewares', includeYear: false },
      verumpkg: { domain: 'infrastructure', entity: 'verumpkgs', includeYear: false },
    };

    return fallbackMap[normalizedKey] ?? null;
  }

  private resolveFallbackDomain(moduleCode?: string, key?: string): string {
    const normalizedModuleCode = (moduleCode || '').toLowerCase();
    const normalizedKey = (key || '').toLowerCase();

    if (normalizedModuleCode === 'compliance-addon-iso22301') {
      return this.resolveIso22301FallbackFeature(normalizedKey)?.domain ?? 'iso22301';
    }

    if (normalizedModuleCode === 'finance-portfolio' || normalizedModuleCode === 'sourcing') {
      return 'finance';
    }

    if (normalizedModuleCode === 'pmo') {
      return normalizedKey === 'projectreporting' ? 'projectreporting' : 'project';
    }

    if (normalizedModuleCode === 'core' || normalizedModuleCode === 'core-parameters') {
      return 'parameter';
    }

    if (normalizedModuleCode === 'core-admin') {
      return 'admin';
    }

    if (normalizedModuleCode.startsWith('compliance')) {
      return 'compliance';
    }

    if (normalizedModuleCode.startsWith('cbm')) {
      if (normalizedKey === 'application' || normalizedKey === 'infrastructure' || normalizedKey === 'business') {
        return normalizedKey;
      }
      return 'business';
    }

    return 'adminportal';
  }

  private createFallbackFeature(key: string, moduleCode?: string): ModuleFeatureDTO {
    const normalizedKey = key.trim();
    const normalizedModuleCode = (moduleCode || '').toLowerCase();

    if (normalizedModuleCode === 'compliance-addon-iso22301') {
      const iso22301Fallback = this.resolveIso22301FallbackFeature(normalizedKey);
      if (iso22301Fallback) {
        const baseRoute = `/${iso22301Fallback.domain}/${iso22301Fallback.entity}`;

        return {
          key: normalizedKey,
          displayName: `features.${normalizedKey}.displayName`,
          subtitle: `features.${normalizedKey}.subtitle`,
          description: `features.${normalizedKey}.description`,
          route: `/adminportal${baseRoute}`,
          portalRoute: `/portal${baseRoute}`,
          includeWorkspaceId: true,
          includeYear: iso22301Fallback.includeYear,
          root: iso22301Fallback.domain,
          enabled: true,
        };
      }
    }

    const domain = this.resolveFallbackDomain(moduleCode, normalizedKey);
    const baseRoute = `/${domain}/${normalizedKey}`;

    return {
      key: normalizedKey,
      displayName: `features.${normalizedKey}.displayName`,
      subtitle: `features.${normalizedKey}.subtitle`,
      description: `features.${normalizedKey}.description`,
      route: baseRoute,
      portalRoute: `/portal${baseRoute}`,
      includeWorkspaceId: true,
      includeYear: true,
      enabled: true,
    };
  }

  private normalizeFeature(rawFeature: unknown, moduleCode?: string): ModuleFeatureDTO | null {
    if (typeof rawFeature === 'string') {
      const key = rawFeature.trim();
      if (key.length === 0) {
        return null;
      }
      return this.createFallbackFeature(key, moduleCode);
    }

    if (!rawFeature || typeof rawFeature !== 'object') {
      return null;
    }

    const candidate = rawFeature as Partial<ModuleFeatureDTO>;
    const key = typeof candidate.key === 'string' ? candidate.key.trim() : '';
    if (key.length === 0) {
      return null;
    }

    const fallbackFeature = this.createFallbackFeature(key, moduleCode);

    const rawRoute = typeof candidate.route === 'string' ? candidate.route.trim() : '';
    const rawPortalRoute = typeof candidate.portalRoute === 'string' ? candidate.portalRoute.trim() : '';

    const shouldUseFallbackRoute =
      rawRoute.length === 0 || rawRoute.startsWith('/adminportal/') || /^\/[^/]+$/.test(rawRoute);
    const shouldUseFallbackPortalRoute =
      rawPortalRoute.length === 0 || /^\/portal\/[^/]+$/.test(rawPortalRoute);

    return {
      key,
      displayName: candidate.displayName || `features.${key}.displayName`,
      subtitle: candidate.subtitle || `features.${key}.subtitle`,
      description: candidate.description || `features.${key}.description`,
      route: shouldUseFallbackRoute ? fallbackFeature.route : rawRoute,
      portalRoute: shouldUseFallbackPortalRoute ? fallbackFeature.portalRoute : rawPortalRoute,
      includeWorkspaceId: candidate.includeWorkspaceId ?? true,
      includeYear: candidate.includeYear ?? true,
      root: candidate.root,
      iconKey: candidate.iconKey,
      requiredPermissions: candidate.requiredPermissions || [],
      order: candidate.order,
      enabled: candidate.enabled ?? true,
    };
  }

  /**
   * Load features from AuthContext response
   * Should be called once on app initialization after auth context loads
   */
  loadFromAuthContext(authContext: AuthContextResponse): void {
    this.features.clear();
    this.moduleFeatures.clear();

    const modules = this.extractModules(authContext);

    modules.forEach((module) => {
      const moduleFeatures: FeatureDefinition[] = [];

      const authFeatures = Array.isArray(module.features) ? module.features : [];
      const fallbackFeatureKeys = authFeatures.length === 0 ? getModuleFeatureKeys(module.code) : [];
      const features: unknown[] =
        authFeatures.length > 0 ? authFeatures : fallbackFeatureKeys.map((featureKey) => this.createFallbackFeature(featureKey, module.code));
      features.forEach((rawFeature) => {
        const normalizedFeature = this.normalizeFeature(rawFeature, module.code);
        if (!normalizedFeature) {
          return;
        }

        // eslint-disable-next-line no-debugger
        // debugger;

        const def: FeatureDefinition = {
          ...normalizedFeature,
          moduleCode: module.code,
          svgIcon: this.resolveIcon(normalizedFeature.iconKey, module.icons),
        };

        this.features.set(normalizedFeature.key, def);
        moduleFeatures.push(def);
      });

      this.moduleFeatures.set(module.code, moduleFeatures);
    });

    this.loaded = true;

    if (typeof window !== 'undefined' && import.meta.env.DEV) {
      console.log(
        `[FeatureRegistry] Loaded ${this.features.size} features from ${modules.length} modules`
      );
    }
  }

  /**
   * Extract modules array from AuthContext
   * Handles both new format (modules) and legacy format (moduleEntitlements)
   */
  private extractModules(authContext: AuthContextResponse): ModuleDefinitionDTO[] {
    // Try new format first
    if (Array.isArray(authContext.modules) && authContext.modules.length > 0) {
      return authContext.modules;
    }

    // Fallback to legacy format (for backward compatibility during migration)
    if (Array.isArray(authContext.moduleEntitlements)) {
      return authContext.moduleEntitlements.map((ent) => ({
        code: ent.moduleCode,
        name: ent.moduleName,
        status: ent.status,
        features: (() => {
          const legacyFeatures = Array.isArray(ent.features) ? ent.features : [];
          const registryFeatures = legacyFeatures.length === 0 ? getModuleFeatureKeys(ent.moduleCode) : [];
          const source = legacyFeatures.length > 0 ? legacyFeatures : registryFeatures;
          return source
            .map((featureKey) =>
              typeof featureKey === 'string' && featureKey.trim().length > 0
                ? this.createFallbackFeature(featureKey, ent.moduleCode)
                : null
            )
            .filter((feature): feature is ModuleFeatureDTO => feature !== null);
        })(),
        icons: undefined,
        menus: [],
        landingMenus: [],
      }));
    }

    return [];
  }

  /**
   * Get feature by key (e.g., "budget", "invoice")
   */
  getFeature(key: string): FeatureDefinition | null {
    if (!key) return null;
    return this.features.get(key) ?? null;
  }

  /**
   * Get all features for a specific module
   */
  getModuleFeatures(moduleCode: string): FeatureDefinition[] {
    return this.moduleFeatures.get(moduleCode) ?? [];
  }

  /**
   * Get features for a site/domain (legacy compatibility)
   * Maps old site names like "finance", "project" to module codes
   */
  getFeaturesForSite(site: string): FeatureDefinition[] {
    if (!site || site.trim() === '') {
      return [];
    }

    const normalizedSite = site.trim().toLowerCase();
    const mappedModuleCodes = SITE_TO_MODULE_MAP[normalizedSite] || [];

    const moduleCodes =
      mappedModuleCodes.length > 0
        ? mappedModuleCodes
        : [
            // Support direct calls with module code or module domain
            ...Array.from(this.moduleFeatures.keys()).filter((code) => code.toLowerCase() === normalizedSite),
            ...this.getAllFeatures()
              .filter((feature) => feature.root?.toLowerCase() === normalizedSite)
              .map((feature) => feature.moduleCode),
          ];

    const features: FeatureDefinition[] = [];
    moduleCodes.forEach((moduleCode) => {
      features.push(...this.getModuleFeatures(moduleCode));
    });

    // Portal/home search should remain useful even if module-code mapping drifts.
    if (features.length === 0 && (normalizedSite === 'portal' || normalizedSite === 'home')) {
      return this.getAllFeatures();
    }

    return features;
  }

  /**
   * Get all features across all modules
   */
  getAllFeatures(): FeatureDefinition[] {
    return Array.from(this.features.values());
  }

  /**
   * Check if registry has been loaded
   */
  hasFeatures(): boolean {
    return this.loaded && this.features.size > 0;
  }

  /**
   * Get feature count (for debugging)
   */
  getFeatureCount(): number {
    return this.features.size;
  }

  /**
   * Get module count (for debugging)
   */
  getModuleCount(): number {
    return this.moduleFeatures.size;
  }

  /**
   * Clear all loaded features (for testing or re-initialization)
   */
  clear(): void {
    this.features.clear();
    this.moduleFeatures.clear();
    this.loaded = false;
  }

  /**
   * Resolve icon URL based on current theme
   */
  private resolveIcon(iconKey?: string, icons?: ModuleIconSet): string | undefined {

    // eslint-disable-next-line no-debugger
    // debugger;

    if (!icons) return undefined;

    const theme = this.getCurrentTheme();
    const iconUrl = theme === 'dark' ? icons.darkIcon : icons.lightIcon;

    return this.normalizeIconUrl(iconUrl || icons.fallbackIcon);
  }

  private normalizeIconUrl(iconUrl?: string): string | undefined {
    if (!iconUrl) {
      return undefined;
    }

    const trimmedUrl = iconUrl.trim();
    if (trimmedUrl.length === 0 || isAbsoluteUrl(trimmedUrl)) {
      return trimmedUrl || undefined;
    }

    const baseUrl = apiAzureUrl() || (typeof window !== 'undefined' ? window.location.origin : '');
    if (!baseUrl) {
      return trimmedUrl;
    }

    try {
      return new URL(trimmedUrl.startsWith('/') ? trimmedUrl : `/${trimmedUrl}`, baseUrl).toString();
    } catch {
      return trimmedUrl;
    }
  }

  /**
   * Get current theme from document
   */
  private getCurrentTheme(): 'light' | 'dark' {
    if (typeof document === 'undefined') {
      return 'light';
    }

    return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
  }

  /**
   * Add or update a single feature (for dynamic module loading)
   */
  addFeature(feature: FeatureDefinition): void {
    this.features.set(feature.key, feature);

    // Update module features map
    const moduleCode = feature.moduleCode;
    const existing = this.moduleFeatures.get(moduleCode) || [];
    const filtered = existing.filter((f) => f.key !== feature.key);
    this.moduleFeatures.set(moduleCode, [...filtered, feature]);
  }

  /**
   * Remove a feature (for dynamic unloading)
   */
  removeFeature(featureKey: string): void {
    const feature = this.features.get(featureKey);
    if (!feature) return;

    this.features.delete(featureKey);

    // Update module features map
    const moduleCode = feature.moduleCode;
    const existing = this.moduleFeatures.get(moduleCode) || [];
    this.moduleFeatures.set(
      moduleCode,
      existing.filter((f) => f.key !== featureKey)
    );
  }
}

/**
 * Singleton instance
 * Import this to access feature registry throughout the application
 */
export const featureRegistry = new FeatureRegistry();

/**
 * React hook for accessing feature registry
 * Ensures registry is loaded before returning features
 */
export const useFeatureRegistry = () => {
  const hasFeatures = featureRegistry.hasFeatures();

  return {
    getFeature: (key: string) => featureRegistry.getFeature(key),
    getModuleFeatures: (moduleCode: string) => featureRegistry.getModuleFeatures(moduleCode),
    getFeaturesForSite: (site: string) => featureRegistry.getFeaturesForSite(site),
    getAllFeatures: () => featureRegistry.getAllFeatures(),
    hasFeatures,
    featureCount: featureRegistry.getFeatureCount(),
    moduleCount: featureRegistry.getModuleCount(),
  };
};
